import type { Breadcrumb, BreadcrumbHint } from '@sentry/react';

const AWS_LINK_QUERY_PARAMS = [
  'response-content-disposition',
  'Expires',
  'Signature',
  'Key-Pair-Id',
];

// SmartVault node URLs name the firm's vault and client folders
// (/nodes/pth/<vault>/<client>/...), and the export picker browses them from
// the browser, so every request leaves one in the breadcrumbs.
const isHostOrSubdomain = (hostname: string, domain: string) =>
  hostname === domain || hostname.endsWith(`.${domain}`);

const isSmartVaultHost = (hostname: string) =>
  isHostOrSubdomain(hostname, 'smartvault.com');

// Graph's @microsoft.graph.downloadUrl is pre-authenticated for about an hour,
// and the OneDrive/SharePoint upload picker fetches it from the browser. The
// credential is in the query on SharePoint/OneDrive for Business
// (download.aspx?UniqueId=…&tempauth=…) and in the path on personal OneDrive
// (*.files.1drv.com/y4m…/name?download). graph.microsoft.com URLs carry no
// credential and stay as they are.
const MICROSOFT_DOWNLOAD_DOMAINS = ['sharepoint.com', '1drv.com', 'livefilestore.com'];

const isMicrosoftDownloadHost = (hostname: string) =>
  MICROSOFT_DOWNLOAD_DOMAINS.some((domain) => isHostOrSubdomain(hostname, domain));

// The Google Drive upload picker downloads and exports files from the browser
// (googleapis.com/drive/v3/files/<file id>?alt=media, …/export?mimeType=…), so
// the path names the user's Drive file. Other googleapis.com APIs stay as
// they are.
const isGoogleDriveUrl = ({ hostname, pathname }: URL) =>
  isHostOrSubdomain(hostname, 'googleapis.com') &&
  (pathname === '/drive' || pathname.startsWith('/drive/'));

/**
 * Called before a breadcrumb is stored: strips signed AWS query strings from
 * request URLs, keeps only the origin of SmartVault URLs (folder paths),
 * Microsoft download URLs (pre-authenticated links) and Google Drive file URLs
 * (file IDs), and adds the request id to xhr breadcrumbs.
 */
export const beforeBreadcrumb = (
  breadcrumb: Breadcrumb,
  hint?: BreadcrumbHint
): Breadcrumb | null => {
  const breadcrumbUrl = breadcrumb?.data?.url
    ? new URL(breadcrumb.data.url)
    : undefined;
  if (!breadcrumbUrl) {
    return breadcrumb;
  }

  const isAWSLink = AWS_LINK_QUERY_PARAMS.every((param) =>
    breadcrumbUrl.search.includes(param)
  );

  /* eslint-disable no-param-reassign */
  if (isAWSLink && breadcrumb?.data?.url) {
    breadcrumbUrl.search = '';
    breadcrumb.data.url = breadcrumbUrl.href;
  }

  if (
    (isSmartVaultHost(breadcrumbUrl.hostname) ||
      isMicrosoftDownloadHost(breadcrumbUrl.hostname) ||
      isGoogleDriveUrl(breadcrumbUrl)) &&
    breadcrumb?.data
  ) {
    breadcrumb.data.url = `${breadcrumbUrl.origin}/[Filtered]`;
  }
  /* eslint-enable no-param-reassign */

  // Add requestId to breadcrumb data
  if (breadcrumb.category === 'xhr') {
    const requestId =
      hint?.xhr?.__sentry_xhr_v2__?.request_headers?.['x-request-id'];
    if (requestId) {
      breadcrumb.data = {
        ...(breadcrumb.data || {}),
        requestId,
      };
    }
  }

  return breadcrumb;
};
